import {
  formatCurrency,
  formatMultiplier,
  formatNumber,
  formatPercent,
  costDiv,
  safeDiv,
} from './formatters'
import { getStatus } from './benchmarks'
import type { MetricStatus, MetricTone, Benchmark, Metric, Recommendation, FunnelMode } from '../types'

export type { FunnelMode }

export interface SonarFunilInputs {
  mode: FunnelMode
  investimento: number
  impressoes: number
  cliques: number
  pageViews: number
  conversasWhatsapp: number
  leadsQualificados: number
  leadsDesqualificados: number
  consultasAgendadas: number
  consultasRealizadas: number
  vendas: number
  ticketMedio: number
}

export interface SonarFunilResult {
  metrics: Metric[]
  metricsByKey: Record<string, Metric>
  primaryMetrics: Metric[]
  secondaryMetrics: Metric[]
  interactionMetrics: Metric[]
  sonarStatus: MetricStatus
  recommendations: Recommendation[]
  bottleneck: string
  bottleneckMetric?: Metric
  totalLeads: number
  faturamento: number
  modeLabel: string
}

const funnelBenchmarks: Record<string, Benchmark> = {
  ctr: { metricId: 'ctr', thresholds: [0.7, 0.5], direction: 'higher-is-better', unit: 'percentage' },
  cliqueParaWhatsapp: { metricId: 'cliqueParaWhatsapp', thresholds: [50, 30], direction: 'higher-is-better', unit: 'percentage' },
  cliqueParaPageView: { metricId: 'cliqueParaPageView', thresholds: [70, 50], direction: 'higher-is-better', unit: 'percentage' },
  pageViewParaLead: { metricId: 'pageViewParaLead', thresholds: [40, 30], direction: 'higher-is-better', unit: 'percentage' },
  leadParaWhatsapp: { metricId: 'leadParaWhatsapp', thresholds: [50, 35], direction: 'higher-is-better', unit: 'percentage' },
  qualificacao: { metricId: 'qualificacao', thresholds: [30, 20], direction: 'higher-is-better', unit: 'percentage' },
  leadParaConsulta: { metricId: 'leadParaConsulta', thresholds: [10, 5], direction: 'higher-is-better', unit: 'percentage' },
  conversaParaConsulta: { metricId: 'conversaParaConsulta', thresholds: [30, 20], direction: 'higher-is-better', unit: 'percentage' },
  agendadaParaRealizada: { metricId: 'agendadaParaRealizada', thresholds: [70, 60], direction: 'higher-is-better', unit: 'percentage' },
  consultaParaVenda: { metricId: 'consultaParaVenda', thresholds: [20, 15], direction: 'higher-is-better', unit: 'percentage' },
  roas: { metricId: 'roas', thresholds: [2, 1.5], direction: 'higher-is-better', unit: 'multiplier' },
}

function buildMetric(
  key: string,
  label: string,
  value: number,
  formatter: (value: number) => string,
  benchmark?: Benchmark,
  tone: MetricTone = 'accent',
  description?: string,
): Metric {
  const status = benchmark ? getStatus(value, benchmark) : tone
  return { key, label, value, formattedValue: formatter(value), status, benchmark, description }
}

function pushRecommendation(
  recommendations: Recommendation[],
  metric: Metric | undefined,
  config: Omit<Recommendation, 'metric' | 'status'>,
) {
  if (!metric || metric.status === 'success' || metric.status === 'accent') return
  recommendations.push({ metric: metric.label, status: metric.status as MetricStatus, ...config })
}

function pickMetrics(metricsByKey: Record<string, Metric>, keys: string[]): Metric[] {
  return keys.map((key) => metricsByKey[key]).filter(Boolean) as Metric[]
}

export function calcularSonarFunil(inputs: SonarFunilInputs): SonarFunilResult {
  const {
    mode, investimento, impressoes, cliques, pageViews,
    conversasWhatsapp, leadsQualificados, leadsDesqualificados,
    consultasAgendadas, consultasRealizadas, vendas, ticketMedio,
  } = inputs

  const totalLeads = leadsQualificados + leadsDesqualificados
  const faturamento = vendas * ticketMedio

  const cpm = safeDiv(investimento, impressoes) * 1000
  const ctr = safeDiv(cliques, impressoes) * 100
  const cpc = costDiv(investimento, cliques)
  const cliqueParaWhatsapp = safeDiv(conversasWhatsapp, cliques) * 100
  const cliqueParaPageView = safeDiv(pageViews, cliques) * 100
  const pageViewParaLead = safeDiv(totalLeads, pageViews) * 100
  const leadParaWhatsapp = safeDiv(conversasWhatsapp, totalLeads) * 100
  const qualificacao = safeDiv(leadsQualificados, totalLeads) * 100
  const leadParaConsulta = safeDiv(consultasAgendadas, leadsQualificados) * 100
  const conversaParaConsulta = safeDiv(consultasAgendadas, conversasWhatsapp) * 100
  const agendadaParaRealizada = safeDiv(consultasRealizadas, consultasAgendadas) * 100
  const consultaParaVenda = safeDiv(vendas, consultasRealizadas) * 100
  const custoConversa = costDiv(investimento, conversasWhatsapp)
  const custoLeadQualificado = costDiv(investimento, leadsQualificados)
  const custoConsulta = costDiv(investimento, consultasAgendadas)
  const custoVenda = costDiv(investimento, vendas)
  const roas = safeDiv(faturamento, investimento)

  const metrics: Metric[] = [
    buildMetric('cpm', 'CPM', cpm, formatCurrency, undefined, 'accent', 'Contexto do leilão. Acompanhe, mas não é a alavanca principal'),
    buildMetric('ctr', 'CTR', ctr, formatPercent, funnelBenchmarks.ctr, 'accent', 'Primeira métrica do criativo: impressão que vira clique'),
    buildMetric('cpc', 'CPC', cpc, formatCurrency, undefined, 'accent', 'Interação entre investimento e cliques'),
    buildMetric('cliqueParaWhatsapp', 'Taxa Clique → WhatsApp', cliqueParaWhatsapp, formatPercent, funnelBenchmarks.cliqueParaWhatsapp, 'accent'),
    buildMetric('cliqueParaPageView', 'Taxa Clique → Page View', cliqueParaPageView, formatPercent, funnelBenchmarks.cliqueParaPageView, 'accent'),
    buildMetric('pageViewParaLead', 'Taxa Page View → Lead', pageViewParaLead, formatPercent, funnelBenchmarks.pageViewParaLead, 'accent'),
    buildMetric('leadParaWhatsapp', 'Taxa Lead → WhatsApp', leadParaWhatsapp, formatPercent, funnelBenchmarks.leadParaWhatsapp, 'accent'),
    buildMetric('qualificacao', 'Taxa de Qualificação', qualificacao, formatPercent, funnelBenchmarks.qualificacao, 'accent'),
    buildMetric(
      'leadParaConsulta',
      'Taxa Lead → Consulta',
      leadParaConsulta,
      formatPercent,
      funnelBenchmarks.leadParaConsulta,
      'accent',
      'Referência: abaixo de 5% é gargalo; de 5% a 9,9% é média; 10% ou mais é boa.',
    ),
    buildMetric('conversaParaConsulta', 'Taxa WhatsApp → Consulta', conversaParaConsulta, formatPercent, funnelBenchmarks.conversaParaConsulta, 'accent'),
    buildMetric('agendadaParaRealizada', 'Taxa Agendada → Realizada', agendadaParaRealizada, formatPercent, funnelBenchmarks.agendadaParaRealizada, 'accent'),
    buildMetric('consultaParaVenda', 'Taxa Consulta → Venda', consultaParaVenda, formatPercent, funnelBenchmarks.consultaParaVenda, 'accent'),
    buildMetric('custoConversa', 'Custo por Conversa', custoConversa, formatCurrency, undefined, 'accent'),
    buildMetric('custoLeadQualificado', 'Custo por Lead Qualificado', custoLeadQualificado, formatCurrency, undefined, 'accent'),
    buildMetric('custoConsulta', 'Custo por Consulta', custoConsulta, formatCurrency, undefined, 'accent'),
    buildMetric('custoVenda', 'Custo por Venda', custoVenda, formatCurrency, undefined, 'accent'),
    buildMetric('totalLeads', 'Total de Leads', totalLeads, formatNumber, undefined, 'accent'),
    buildMetric('faturamento', 'Faturamento', faturamento, formatCurrency, undefined, 'accent'),
    buildMetric('roas', 'ROAS', roas, formatMultiplier, funnelBenchmarks.roas, 'accent', 'Resultado econômico do funil depois da venda'),
  ]

  const metricsByKey = Object.fromEntries(metrics.map((m) => [m.key, m])) as Record<string, Metric>

  const primaryMetricKeys =
    mode === 'landing-page'
      ? ['ctr', 'cliqueParaPageView', 'pageViewParaLead', 'leadParaWhatsapp', 'qualificacao', 'conversaParaConsulta', 'agendadaParaRealizada', 'consultaParaVenda', 'roas']
      : ['ctr', 'cliqueParaWhatsapp', 'qualificacao', 'leadParaConsulta', 'agendadaParaRealizada', 'consultaParaVenda', 'roas']

  const primaryMetrics = pickMetrics(metricsByKey, primaryMetricKeys)
  const secondaryMetrics = pickMetrics(metricsByKey, ['cpm', 'cpc'])
  const interactionMetricKeys =
    mode === 'landing-page'
      ? ['custoLeadQualificado', 'custoConsulta', 'custoVenda', 'faturamento']
      : ['custoConversa', 'custoLeadQualificado', 'custoConsulta', 'custoVenda', 'faturamento']
  const interactionMetrics = pickMetrics(metricsByKey, interactionMetricKeys)

  const successCount = primaryMetrics.filter((m) => m.status === 'success').length
  const dangerCount = primaryMetrics.filter((m) => m.status === 'danger').length

  let sonarStatus: MetricStatus
  if (dangerCount >= 2) sonarStatus = 'danger'
  else if (successCount >= Math.ceil(primaryMetrics.length * 0.7)) sonarStatus = 'success'
  else sonarStatus = 'warning'

  const bottleneckMetric =
    primaryMetrics.find((m) => m.status === 'danger') ??
    primaryMetrics.find((m) => m.status === 'warning')

  const recommendations: Recommendation[] = []

  pushRecommendation(recommendations, metricsByKey.ctr, {
    causes: ['Gancho fraco nos primeiros segundos do criativo', 'Ângulo do anúncio não conversa com dor, desejo ou nível de consciência do público', 'Promessa genérica, oferta pouco clara ou criativo já saturado'],
    actions: ['Reescrever abertura com dor, transformação ou prova específica', 'Testar novos ângulos, ganchos e formatos para o mesmo público', 'Comparar CTR por criativo antes de trocar segmentação ou orçamento'],
    detail: 'CTR baixo normalmente é problema de mensagem ou criativo antes de qualquer outro gargalo do funil.',
  })

  if (mode === 'landing-page') {
    pushRecommendation(recommendations, metricsByKey.cliqueParaPageView, {
      causes: ['Página lenta no mobile ou erro no carregamento', 'Anúncio promete algo que a primeira dobra não confirma', 'Redirecionamento, pixel ou página intermediária criando perda técnica'],
      actions: ['Medir carregamento mobile e eliminar elementos pesados', 'Repetir na headline a promessa central do anúncio', 'Validar links, redirecionamentos e eventos de page view'],
      detail: 'Quando o clique não vira page view, o investimento se perde antes de o lead existir.',
    })
    pushRecommendation(recommendations, metricsByKey.pageViewParaLead, {
      causes: ['Oferta não justifica deixar contato para esse nível de consciência', 'Formulário pede informação demais ou gera insegurança', 'Página explica o serviço, mas não vende transformação, prova ou próximo passo'],
      actions: ['Manter no formulário apenas dados necessários para a primeira conversa', 'Levar prova, benefício e CTA para a primeira dobra', 'Testar copy que nomeie problema, transformação e ação seguinte'],
      detail: 'Essa taxa mostra se a landing page transforma atenção em oportunidade real.',
    })
    pushRecommendation(recommendations, metricsByKey.leadParaWhatsapp, {
      causes: ['Lead não entende por que deve continuar no WhatsApp', 'Mensagem pós-cadastro demora, parece automática ou não retoma o contexto', 'A ponte entre página, conversa e consulta ficou confusa'],
      actions: ['Explicar no obrigado qual transformação a conversa vai destravar', 'Enviar primeira mensagem com contexto, pergunta diagnóstica e próximo passo', 'Usar follow-up com prova e CTA quando não houver resposta'],
      detail: 'No modelo com landing page, o lead ainda precisa avançar para uma conversa com propósito.',
    })
    pushRecommendation(recommendations, metricsByKey.conversaParaConsulta, {
      causes: ['Resposta lenta deixa o interesse esfriar', 'Atendente atua como suporte ou tirador de pedido, sem conduzir a transformação que o cliente busca', 'Qualificação coleta dados, mas não explora dor, objetivo, urgência e impacto de não agir', 'Convite para consulta não posiciona valor nem deixa próximo passo claro'],
      actions: ['Definir SLA de primeira resposta e acompanhar conversas fora do prazo', 'Usar roteiro consultivo: diagnóstico breve → transformação desejada → impacto → convite para consulta', 'Ouvir conversas e corrigir pontos em que o atendente apenas responde preço ou disponibilidade', 'Apresentar a consulta como etapa para construir um plano, não como simples horário na agenda'],
      detail: 'Depois que o lead entrou na conversa, a operação precisa vender valor da consulta e conduzir compromisso — não apenas responder pedidos.',
    })
  } else {
    pushRecommendation(recommendations, metricsByKey.cliqueParaWhatsapp, {
      causes: ['CTA do criativo não deixa claro que o próximo passo é uma conversa', 'Pessoa chega ao WhatsApp sem contexto ou sem mensagem inicial', 'Promessa do anúncio e primeira mensagem parecem duas experiências diferentes'],
      actions: ['Explicar no criativo o que acontece ao clicar e qual benefício a conversa entrega', 'Usar mensagem pré-preenchida que retome interesse ou objetivo', 'Auditar links, eventos e primeira mensagem para garantir continuidade'],
      detail: 'Essa é uma das porcentagens mais importantes do modelo direto para WhatsApp: ela mede se a intenção virou conversa.',
    })
    pushRecommendation(recommendations, metricsByKey.leadParaConsulta, {
      causes: ['Lead qualificado não recebe condução rápida para a consulta', 'Atendente atua como suporte ou tirador de pedido, sem vender a transformação que o cliente busca', 'Qualificação coleta informações, mas não explora dor, objetivo, urgência e impacto de não agir', 'Equipe conversa, responde preço ou disponibilidade e não faz convite claro para agendar', 'Follow-up termina após a primeira objeção ou ausência de resposta'],
      actions: ['Definir SLA de primeira resposta e encurtar o caminho até o convite', 'Usar roteiro consultivo: diagnóstico breve → transformação desejada → impacto → convite para consulta', 'Ouvir conversas e marcar onde o atendente deixou de conduzir o agendamento', 'Apresentar a consulta como etapa para construir um plano, não como simples horário disponível', 'Criar follow-up com prova, urgência legítima e CTA de agenda para quem não decide na hora'],
      detail: 'Abaixo de 5% é gargalo; de 5% a 9,9% é média; 10% ou mais é boa. Aqui a prioridade é vender valor da consulta e conduzir compromisso, não apenas tirar pedido.',
    })
  }

  pushRecommendation(recommendations, metricsByKey.qualificacao, {
    causes: ['Criativo atrai curiosos em vez de pacientes adequados', 'Oferta ampla demais para procedimento, perfil ou capacidade de investimento', 'Filtro inicial deixa de identificar momento, necessidade e elegibilidade'],
    actions: ['Especificar no anúncio para quem é o procedimento e qual problema resolve', 'Inserir perguntas simples sobre objetivo, momento e critério de elegibilidade', 'Remover iscas que geram volume sem intenção e revisar origem dos leads ruins'],
    detail: 'Lead barato sem qualificação só transfere trabalho improdutivo para a operação comercial.',
  })

  pushRecommendation(recommendations, metricsByKey.agendadaParaRealizada, {
    causes: ['Falta de confirmação, lembrete ou reconfirmação ativa', 'Tempo longo entre agendamento e consulta', 'Paciente aceitou horário sem perceber valor, preparo ou consequência de faltar'],
    actions: ['Implementar confirmação automática e reconfirmação humana para casos de risco', 'Reduzir janela até a consulta e oferecer reposição rápida quando necessário', 'Enviar preparo, prova e motivo concreto para comparecer no lembrete'],
    detail: 'Taxa de comparecimento baixa desperdiça mídia, atendimento e agenda já investidos.',
  })

  pushRecommendation(recommendations, metricsByKey.consultaParaVenda, {
    causes: ['Consulta explica procedimento, mas não constrói valor da transformação para aquele paciente', 'Diagnóstico, prova ou plano não respondem às objeções centrais', 'Oferta final não encaixa na expectativa criada pelo tráfego ou na capacidade de decisão do paciente'],
    actions: ['Revisar roteiro para conectar diagnóstico, consequência, plano e transformação esperada', 'Mapear objeções recorrentes por gravações e treinar respostas com a equipe', 'Alinhar promessa de tráfego, atendimento e consulta para não criar quebra de expectativa'],
    detail: 'Se a consulta não vira venda, o restante do funil pode funcionar e ainda assim o caixa não aparece.',
  })

  pushRecommendation(recommendations, metricsByKey.roas, {
    causes: ['Custo do funil alto para ticket e margem atuais', 'Venda final insuficiente para compensar mídia e operação', 'Perdas acumuladas em etapas anteriores corroem resultado econômico'],
    actions: ['Corrigir primeiro a métrica primária com pior status', 'Revisar ticket, margem e composição da oferta antes de escalar investimento', 'Acompanhar custo por venda junto de consulta → venda e margem de contribuição'],
    detail: 'ROAS não é a alavanca inicial, mas confirma se o funil está saudável economicamente.',
  })

  return {
    metrics,
    metricsByKey,
    primaryMetrics,
    secondaryMetrics,
    interactionMetrics,
    sonarStatus,
    recommendations,
    bottleneck: bottleneckMetric?.label ?? 'Sem gargalo crítico aparente',
    bottleneckMetric,
    totalLeads,
    faturamento,
    modeLabel: mode === 'landing-page' ? 'Landing Page' : 'WhatsApp Direto',
  }
}

export function getSonarLabel(status: MetricStatus): string {
  if (status === 'success') return 'Funil saudável'
  if (status === 'warning') return 'Funil com atrito'
  return 'Funil travado'
}
